import {
  SettingsHelp,
  SettingsSaveBar,
  SettingsSection,
} from "../../components/proposals/ProposalSettingsUI";
import { RepairTaskLibrary } from "./RepairTaskLibrary";
import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Textarea,
  Typography,
} from "@mui/joy";
import "./dealProposals.css";
import { ShieldCheck, Wrench } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import {
  proposalDealsApi,
  dealError,
  type DealSettings,
} from "../../lib/proposalDealsApi";

function Editor({
  settings,
  onDirtyChange,
}: {
  settings: DealSettings;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const client = useQueryClient();
  const [value, setValue] = useState(settings);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [pending, setPending] = useState(false);
  const [baseline, setBaseline] = useState(settings);
  const dirty = JSON.stringify(value) !== JSON.stringify(baseline);
  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);
  const save = async () => {
    setPending(true);
    setError("");
    setSaved(false);
    try {
      await proposalDealsApi.saveSettings({
        insurance_enabled: value.insurance_enabled,
        insurance_terms: value.insurance_terms,
        repair_enabled: value.repair_enabled,
        contingency_template: value.contingency_template,
        repair_terms: value.repair_terms,
        inspection_requirements: value.inspection_requirements,
      });
      await client.invalidateQueries({ queryKey: ["proposal-deal-settings"] });
      setSaved(true);
      setBaseline(value);
    } catch (e) {
      setError(dealError(e));
    } finally {
      setPending(false);
    }
  };
  return (
    <Stack className="deal-workspace deal-settings" spacing={2.5}>
      <Typography level="h4">Insurance & Repair workflows</Typography>
      <Typography level="body-sm">
        Insurance and Repairs start with default settings. Customize them to
        match your process. Existing proposals retain their captured content.
      </Typography>
      {!settings.can_manage && (
        <Alert>An organization administrator can change these settings.</Alert>
      )}
      {error && <Alert color="danger">{error}</Alert>}
      {saved && <Alert color="success">Workflow settings saved.</Alert>}
      <SettingsSection
        title="Insurance"
        description="Inspection authorization, agreement terms, and contingency templates."
        icon={<ShieldCheck size={24} />}
      >
        <Stack spacing={2}>
          <Typography
            level="title-lg"
            startDecorator={<ShieldCheck size={23} />}
          >
            Insurance
          </Typography>
          <Typography level="body-sm">
            Inspection evidence, report sharing, and contingency signing.
          </Typography>
          {!settings.insurance_configured && (
            <Alert variant="soft">
              Using default Insurance settings. Edit the agreement or checklist
              below to match your process.
            </Alert>
          )}
          <Checkbox
            label="Enable Insurance"
            checked={value.insurance_enabled}
            disabled={!settings.can_manage}
            onChange={(e) =>
              setValue({ ...value, insurance_enabled: e.target.checked })
            }
          />
          <FormControl>
            <FormLabel>Published contingency template</FormLabel>
            <select
              aria-label="Published contingency template"
              disabled={!settings.can_manage}
              value={value.contingency_template ?? ""}
              onChange={(e) =>
                setValue({
                  ...value,
                  contingency_template: e.target.value
                    ? Number(e.target.value)
                    : null,
                })
              }
              style={{
                padding: 12,
                background: "var(--joy-palette-background-surface)",
                color: "var(--joy-palette-text-primary)",
                border: "1px solid var(--joy-palette-divider)",
                borderRadius: 8,
              }}
            >
              <option value="">Default inspection authorization</option>
              {settings.templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} · v{t.published_version}
                </option>
              ))}
            </select>
          </FormControl>
          <Typography level="body-sm">
            Use the included inspection authorization or choose your
            organization’s published contingency agreement from{" "}
            <Link to="/crm/contracts">Contracts</Link>.
          </Typography>
          {!value.contingency_template && (
            <FormControl>
              <FormLabel>Default Insurance agreement terms</FormLabel>
              <Textarea
                minRows={6}
                value={value.insurance_terms}
                disabled={!settings.can_manage}
                onChange={(e) =>
                  setValue({ ...value, insurance_terms: e.target.value })
                }
              />
            </FormControl>
          )}
        </Stack>
      </SettingsSection>
      <SettingsSection
        title="Repair"
        description="Enable repair proposals and customize the authorization terms."
        icon={<Wrench size={24} />}
      >
        <Stack spacing={2}>
          <Typography level="title-lg" startDecorator={<Wrench size={23} />}>
            Repair
          </Typography>
          <Typography level="body-sm">
            Defined scope, agreed pricing, and repair authorization.
          </Typography>
          {!settings.repair_configured && (
            <Alert variant="soft">
              Using default Repair settings. Edit the authorization terms below
              to match your process.
            </Alert>
          )}
          <Checkbox
            label="Enable Repair"
            checked={value.repair_enabled}
            disabled={!settings.can_manage}
            onChange={(e) =>
              setValue({ ...value, repair_enabled: e.target.checked })
            }
          />
          <FormControl>
            <FormLabel>Default repair authorization terms</FormLabel>
            <Textarea
              minRows={4}
              value={value.repair_terms}
              disabled={!settings.can_manage}
              onChange={(e) =>
                setValue({ ...value, repair_terms: e.target.value })
              }
            />
          </FormControl>
        </Stack>
      </SettingsSection>
      <SettingsSection
        title="Repair task library"
        description="Save common repairs, descriptions, and prices for quick reuse."
      >
        <RepairTaskLibrary manage />
      </SettingsSection>
      <SettingsSection
        title="Inspection checklist"
        description={`${value.inspection_requirements.length} requirements · photos and reviewed exceptions for new inspections.`}
      >
        <Stack spacing={2}>
          <Typography level="title-lg">
            Inspection checklist defaults
          </Typography>
          <Typography level="body-sm">
            Changes apply to new inspections. Existing checklists and reports
            remain preserved.
          </Typography>
          {value.inspection_requirements.map((r, i) => (
            <div className="deal-settings-row" key={i}>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Requirement</FormLabel>
                <Input
                  value={r.title}
                  disabled={!settings.can_manage}
                  onChange={(e) =>
                    setValue({
                      ...value,
                      inspection_requirements:
                        value.inspection_requirements.map((row, index) =>
                          index === i ? { ...row, title: e.target.value } : row,
                        ),
                    })
                  }
                />
              </FormControl>
              <FormControl>
                <FormLabel>
                  Minimum photos{" "}
                  <SettingsHelp title="Inspection evidence">
                    Number of photos needed for this requirement. Allow reviewed
                    exception lets an authorized user document why it cannot be
                    met.
                  </SettingsHelp>
                </FormLabel>
                <Input
                  type="number"
                  slotProps={{ input: { min: 0, max: 30 } }}
                  value={r.minimum_photos}
                  disabled={!settings.can_manage}
                  onChange={(e) =>
                    setValue({
                      ...value,
                      inspection_requirements:
                        value.inspection_requirements.map((row, index) =>
                          index === i
                            ? { ...row, minimum_photos: Number(e.target.value) }
                            : row,
                        ),
                    })
                  }
                />
              </FormControl>
              <Checkbox
                sx={{ mt: { sm: 3 } }}
                label="Allow reviewed exception"
                checked={r.allow_exception}
                disabled={!settings.can_manage}
                onChange={(e) =>
                  setValue({
                    ...value,
                    inspection_requirements: value.inspection_requirements.map(
                      (row, index) =>
                        index === i
                          ? { ...row, allow_exception: e.target.checked }
                          : row,
                    ),
                  })
                }
              />
              <Button
                variant="plain"
                disabled={
                  !settings.can_manage ||
                  value.inspection_requirements.length === 1
                }
                onClick={() =>
                  setValue({
                    ...value,
                    inspection_requirements:
                      value.inspection_requirements.filter(
                        (_, index) => index !== i,
                      ),
                  })
                }
              >
                Remove
              </Button>
            </div>
          ))}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            gap={2}
          >
            <Button
              variant="outlined"
              disabled={
                !settings.can_manage ||
                value.inspection_requirements.length >= 50
              }
              onClick={() =>
                setValue({
                  ...value,
                  inspection_requirements: [
                    ...value.inspection_requirements,
                    {
                      title: "New requirement",
                      minimum_photos: 1,
                      allow_exception: false,
                    },
                  ],
                })
              }
            >
              Add requirement
            </Button>
          </Stack>
        </Stack>
      </SettingsSection>
      <SettingsSaveBar
        dirty={dirty}
        saving={pending}
        disabled={!settings.can_manage}
        onSave={() => void save()}
        label="Save workflow settings"
      />
    </Stack>
  );
}
export default function DealSettingsPanel({
  onDirtyChange,
}: {
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const query = useQuery({
    queryKey: ["proposal-deal-settings"],
    queryFn: proposalDealsApi.settings,
  });
  if (query.isError)
    return (
      <Alert
        color="danger"
        endDecorator={
          <Button onClick={() => void query.refetch()}>Retry</Button>
        }
      >
        Workflow settings could not be loaded.
      </Alert>
    );
  return query.data ? (
    <Editor settings={query.data} onDirtyChange={onDirtyChange} />
  ) : (
    <Typography>Loading workflow settings…</Typography>
  );
}
